import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { 
  Wrench, 
  Users, 
  ClipboardCheck, 
  Plus, 
  Clock, 
  CheckCircle2, 
  AlertTriangle,
  User,
  Loader2,
  Target,
  MapPin,
  Brain,
  Filter,
  ChevronRight,
  AlertCircle,
  Search
} from "lucide-react";
import { format, formatDistanceToNow } from "date-fns";
import { cn } from "@/lib/utils";

type TaskCategory = 'fix' | 'coach' | 'inspect' | 'general';

interface SupervisorTask {
  id: string;
  title: string;
  description: string | null;
  priority: string;
  status: string;
  source_type: string;
  source_id: string | null;
  due_date: string | null;
  created_at: string;
  assigned_to: string | null;
  verification_required: boolean | null;
  site_id: string | null;
  company_id: string | null;
  task_category: TaskCategory;
  target_worker_id: string | null;
  urgency_score: number | null;
  source_context: {
    worker_name?: string;
    worker_risk_score?: number;
    worker_safety_state?: string;
    ai_explanation?: string;
    location?: string;
    hazard_category?: string;
    report_count?: number;
    friction_score?: number;
  } | null;
  completion_notes: string | null;
  // Joined data
  assigned_supervisor?: {
    display_name: string | null;
  } | null;
  target_worker?: {
    display_name: string | null;
    current_risk_score: number | null;
    safety_state: string | null;
  } | null;
}

interface Supervisor {
  id: string;
  display_name: string | null;
  user_id: string;
}

interface SupervisorActionProps {
  siteId?: string | null;
}

export default function SupervisorAction({ siteId }: SupervisorActionProps = {}) {
  const queryClient = useQueryClient();
  const [activeCategory, setActiveCategory] = useState<TaskCategory | 'all'>('all');
  const [statusFilter, setStatusFilter] = useState<string>("pending");
  const [supervisorFilter, setSupervisorFilter] = useState<string>("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [selectedTask, setSelectedTask] = useState<SupervisorTask | null>(null);
  const [completionNotes, setCompletionNotes] = useState("");
  const [newTask, setNewTask] = useState({
    title: "",
    description: "",
    priority: "medium",
    task_category: "inspect" as TaskCategory,
    assigned_to: ""
  });

  // Fetch supervisors for assignment dropdown
  const { data: supervisors } = useQuery({
    queryKey: ["supervisors", siteId],
    queryFn: async () => {
      let query = supabase
        .from("worker_profiles")
        .select("id, display_name, user_id")
        .eq("role", "supervisor");
      
      if (siteId) {
        query = query.eq("site_id", siteId);
      }
      
      const { data, error } = await query;
      if (error) throw error;
      return data as Supervisor[];
    }
  });

  // Fetch supervisor tasks (Fix, Coach, Inspect only - NO verify)
  const { data: tasks, isLoading: tasksLoading, refetch: refetchTasks } = useQuery({
    queryKey: ["supervisor-action-tasks", activeCategory, statusFilter, supervisorFilter, siteId, searchQuery],
    queryFn: async () => {
      let query = supabase
        .from("supervisor_tasks")
        .select(`
          *,
          assigned_supervisor:worker_profiles!assigned_to(display_name),
          target_worker:worker_profiles!target_worker_id(display_name, current_risk_score, safety_state)
        `)
        .in("task_category", ["fix", "coach", "inspect"])
        .order("urgency_score", { ascending: false })
        .order("created_at", { ascending: false });
      
      if (activeCategory !== 'all') {
        query = query.eq("task_category", activeCategory);
      }
      
      if (statusFilter !== "all") {
        query = query.eq("status", statusFilter);
      }

      if (supervisorFilter !== "all") {
        query = query.eq("assigned_to", supervisorFilter);
      }

      if (siteId) {
        query = query.eq("site_id", siteId);
      }
      
      const { data, error } = await query;
      if (error) throw error;
      
      // Client-side search filter
      let filteredData = data as SupervisorTask[];
      if (searchQuery) {
        const lowerSearch = searchQuery.toLowerCase();
        filteredData = filteredData.filter(t => 
          t.title.toLowerCase().includes(lowerSearch) ||
          t.description?.toLowerCase().includes(lowerSearch) ||
          t.source_context?.worker_name?.toLowerCase().includes(lowerSearch) ||
          t.source_context?.location?.toLowerCase().includes(lowerSearch)
        );
      }
      
      return filteredData;
    }
  });

  // Create task mutation
  const createTaskMutation = useMutation({
    mutationFn: async (task: typeof newTask) => {
      // Get company_id from user profile
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data: profile } = await supabase
        .from('worker_profiles')
        .select('company_id')
        .eq('user_id', user.id)
        .single();

      if (!profile?.company_id) {
        throw new Error('Could not determine company');
      }

      const { error } = await supabase
        .from("supervisor_tasks")
        .insert({
          company_id: profile.company_id,
          site_id: siteId || null,
          title: task.title,
          description: task.description,
          priority: task.priority,
          task_category: task.task_category,
          source_type: "manual",
          status: "pending",
          assigned_to: task.assigned_to && task.assigned_to !== 'unassigned' ? task.assigned_to : null,
        });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["supervisor-action-tasks"] });
      setShowCreateDialog(false);
      setNewTask({ title: "", description: "", priority: "medium", task_category: "inspect", assigned_to: "" });
      toast.success("Supervisor action created");
    },
    onError: () => toast.error("Failed to create action")
  });

  // Update task status mutation
  const updateTaskMutation = useMutation({
    mutationFn: async ({ id, status, notes }: { id: string; status: string; notes?: string }) => {
      const updates: Record<string, unknown> = { status };
      if (status === "completed") {
        updates.completed_at = new Date().toISOString();
        if (notes) updates.completion_notes = notes;
      }
      const { error } = await supabase
        .from("supervisor_tasks")
        .update(updates)
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["supervisor-action-tasks"] });
      setSelectedTask(null);
      setCompletionNotes("");
      toast.success("Action updated");
    }
  });

  // Assign supervisor mutation
  const assignSupervisorMutation = useMutation({
    mutationFn: async ({ taskId, supervisorId }: { taskId: string; supervisorId: string | null }) => {
      const { error } = await supabase
        .from("supervisor_tasks")
        .update({ assigned_to: supervisorId })
        .eq("id", taskId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["supervisor-action-tasks"] });
      toast.success("Supervisor assigned");
    }
  });

  const getCategoryIcon = (category: TaskCategory) => {
    switch (category) {
      case 'fix': return <Wrench className="h-4 w-4" />;
      case 'coach': return <Users className="h-4 w-4" />;
      case 'inspect': return <ClipboardCheck className="h-4 w-4" />;
      default: return <Target className="h-4 w-4" />;
    }
  };

  const getCategoryColor = (category: TaskCategory) => {
    switch (category) {
      case 'fix': return 'bg-orange-500/10 text-orange-600 border-orange-300';
      case 'coach': return 'bg-purple-500/10 text-purple-600 border-purple-300';
      case 'inspect': return 'bg-blue-500/10 text-blue-600 border-blue-300';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case "critical": return "bg-destructive text-destructive-foreground";
      case "high": return "bg-orange-500 text-white";
      case "medium": return "bg-yellow-500 text-black";
      case "low": return "bg-muted text-muted-foreground";
      default: return "bg-muted text-muted-foreground";
    }
  };

  const getSourceLabel = (sourceType: string) => {
    switch (sourceType) {
      case 'friction_map': return 'Friction Map';
      case 'mdp_recommendation': return 'AI Recommender';
      case 'hazard_escalation': return 'Hazard Escalation';
      case 'manual': return 'Manual';
      default: return sourceType;
    }
  };

  // Stats
  const taskStats = {
    fix: tasks?.filter(t => t.task_category === 'fix' && t.status === 'pending').length || 0,
    coach: tasks?.filter(t => t.task_category === 'coach' && t.status === 'pending').length || 0,
    inspect: tasks?.filter(t => t.task_category === 'inspect' && t.status === 'pending').length || 0,
    unassigned: tasks?.filter(t => !t.assigned_to && t.status === 'pending').length || 0,
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Target className="h-6 w-6 text-primary" />
            Supervisor Action
          </h2>
          <p className="text-muted-foreground">
            Manage supervisor tasks: Fix hazards, Coach workers, Inspect locations
          </p>
        </div>
        <Button onClick={() => setShowCreateDialog(true)} className="gap-2">
          <Plus className="h-4 w-4" />
          Create Action
        </Button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-orange-500/20 bg-orange-500/5">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-orange-500/10">
                <Wrench className="h-5 w-5 text-orange-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{taskStats.fix}</p>
                <p className="text-xs text-muted-foreground">Fix Tasks</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-purple-500/20 bg-purple-500/5">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-purple-500/10">
                <Users className="h-5 w-5 text-purple-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{taskStats.coach}</p>
                <p className="text-xs text-muted-foreground">Coach Tasks</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-blue-500/20 bg-blue-500/5">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-500/10">
                <ClipboardCheck className="h-5 w-5 text-blue-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{taskStats.inspect}</p>
                <p className="text-xs text-muted-foreground">Inspect Tasks</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className={cn(taskStats.unassigned > 0 && "border-warning/50 bg-warning/5")}>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-warning/10">
                <AlertCircle className="h-5 w-5 text-warning" />
              </div>
              <div>
                <p className="text-2xl font-bold">{taskStats.unassigned}</p>
                <p className="text-xs text-muted-foreground">Unassigned</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search actions..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[140px]">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="in_progress">In Progress</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
          </SelectContent>
        </Select>
        <Select value={supervisorFilter} onValueChange={setSupervisorFilter}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Supervisor" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Supervisors</SelectItem>
            <SelectItem value="unassigned">Unassigned Only</SelectItem>
            {supervisors?.map(sup => (
              <SelectItem key={sup.id} value={sup.id}>
                {sup.display_name || 'Unknown'}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Category Tabs */}
      <Tabs value={activeCategory} onValueChange={(v) => setActiveCategory(v as TaskCategory | 'all')}>
        <TabsList className="grid w-full grid-cols-4 max-w-lg">
          <TabsTrigger value="all" className="gap-2">
            All
            <Badge variant="secondary" className="ml-1">
              {(taskStats.fix + taskStats.coach + taskStats.inspect)}
            </Badge>
          </TabsTrigger>
          <TabsTrigger value="fix" className="gap-2">
            <Wrench className="h-4 w-4" />
            Fix
            {taskStats.fix > 0 && <Badge variant="secondary">{taskStats.fix}</Badge>}
          </TabsTrigger>
          <TabsTrigger value="coach" className="gap-2">
            <Users className="h-4 w-4" />
            Coach
            {taskStats.coach > 0 && <Badge variant="secondary">{taskStats.coach}</Badge>}
          </TabsTrigger>
          <TabsTrigger value="inspect" className="gap-2">
            <ClipboardCheck className="h-4 w-4" />
            Inspect
            {taskStats.inspect > 0 && <Badge variant="secondary">{taskStats.inspect}</Badge>}
          </TabsTrigger>
        </TabsList>

        <TabsContent value={activeCategory} className="mt-4">
          {tasksLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : !tasks || tasks.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Target className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
                <h3 className="font-semibold text-lg mb-2">No Supervisor Actions</h3>
                <p className="text-muted-foreground mb-4">
                  Create actions from Friction Map, AI Recommendations, or manually.
                </p>
                <Button onClick={() => setShowCreateDialog(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Create Action
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              {tasks.map((task) => (
                <Card 
                  key={task.id} 
                  className={cn(
                    "hover:border-primary/50 transition-colors cursor-pointer",
                    task.status === 'completed' && "opacity-60"
                  )}
                  onClick={() => setSelectedTask(task)}
                >
                  <CardContent className="py-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-start gap-3 flex-1">
                        <div className={cn("p-2 rounded-lg", getCategoryColor(task.task_category))}>
                          {getCategoryIcon(task.task_category)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap mb-1">
                            <Badge variant="outline" className={getCategoryColor(task.task_category)}>
                              {task.task_category.toUpperCase()}
                            </Badge>
                            <Badge className={getPriorityColor(task.priority)}>
                              {task.priority}
                            </Badge>
                            <Badge variant="outline" className="text-xs">
                              {getSourceLabel(task.source_type)}
                            </Badge>
                          </div>
                          
                          <h4 className="font-medium">{task.title}</h4>
                          
                          {task.description && (
                            <p className="text-sm text-muted-foreground line-clamp-2 mt-1">
                              {task.description}
                            </p>
                          )}

                          {/* Context info */}
                          <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground">
                            {task.target_worker && (
                              <div className="flex items-center gap-1">
                                <User className="h-3 w-3" />
                                <span>{task.target_worker.display_name}</span>
                                {task.target_worker.current_risk_score && (
                                  <Badge 
                                    variant="outline" 
                                    className={cn(
                                      "text-xs ml-1",
                                      task.target_worker.current_risk_score >= 70 
                                        ? "text-destructive border-destructive/50" 
                                        : task.target_worker.current_risk_score >= 40 
                                        ? "text-warning border-warning/50"
                                        : "text-success border-success/50"
                                    )}
                                  >
                                    {task.target_worker.current_risk_score}%
                                  </Badge>
                                )}
                              </div>
                            )}
                            {task.source_context?.location && (
                              <div className="flex items-center gap-1">
                                <MapPin className="h-3 w-3" />
                                <span>{task.source_context.location}</span>
                              </div>
                            )}
                            <div className="flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              <span>{formatDistanceToNow(new Date(task.created_at))} ago</span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Right side: Assignment & Status */}
                      <div className="flex flex-col items-end gap-2" onClick={(e) => e.stopPropagation()}>
                        <Select
                          value={task.assigned_to || "unassigned"}
                          onValueChange={(value) => {
                            assignSupervisorMutation.mutate({
                              taskId: task.id,
                              supervisorId: value === "unassigned" ? null : value
                            });
                          }}
                        >
                          <SelectTrigger className={cn(
                            "w-[160px] text-xs",
                            !task.assigned_to && "border-warning text-warning"
                          )}>
                            <SelectValue placeholder="Assign supervisor" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="unassigned">Unassigned</SelectItem>
                            {supervisors?.map(sup => (
                              <SelectItem key={sup.id} value={sup.id}>
                                {sup.display_name || 'Unknown'}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        
                        {task.status === 'pending' && (
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={(e) => {
                              e.stopPropagation();
                              updateTaskMutation.mutate({ id: task.id, status: 'in_progress' });
                            }}
                          >
                            Start
                          </Button>
                        )}
                        {task.status === 'in_progress' && (
                          <Badge className="bg-blue-500">In Progress</Badge>
                        )}
                        {task.status === 'completed' && (
                          <Badge className="bg-success"><CheckCircle2 className="h-3 w-3 mr-1" />Done</Badge>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Task Detail Dialog */}
      <Dialog open={!!selectedTask} onOpenChange={() => setSelectedTask(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <div className="flex items-center gap-2">
              <div className={cn("p-2 rounded-lg", getCategoryColor(selectedTask?.task_category || 'general'))}>
                {getCategoryIcon(selectedTask?.task_category || 'general')}
              </div>
              <div>
                <DialogTitle>{selectedTask?.title}</DialogTitle>
                <DialogDescription>
                  {selectedTask?.task_category.toUpperCase()} • {getSourceLabel(selectedTask?.source_type || '')}
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>

          <div className="space-y-4">
            {selectedTask?.description && (
              <div>
                <h4 className="text-sm font-medium mb-1">Description</h4>
                <p className="text-sm text-muted-foreground">{selectedTask.description}</p>
              </div>
            )}

            {/* AI Explanation for Coach tasks */}
            {selectedTask?.task_category === 'coach' && selectedTask.source_context?.ai_explanation && (
              <div className="bg-purple-50 dark:bg-purple-950/20 rounded-lg p-3 border border-purple-200 dark:border-purple-800">
                <div className="flex items-start gap-2">
                  <Brain className="h-4 w-4 text-purple-600 mt-0.5" />
                  <div>
                    <h4 className="text-sm font-medium text-purple-700 dark:text-purple-300 mb-1">AI Recommendation</h4>
                    <p className="text-sm text-purple-600 dark:text-purple-400">{selectedTask.source_context.ai_explanation}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Worker Info for Coach tasks */}
            {selectedTask?.target_worker && (
              <div>
                <h4 className="text-sm font-medium mb-2">Target Worker</h4>
                <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                  <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <User className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <p className="font-medium">{selectedTask.target_worker.display_name}</p>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      {selectedTask.target_worker.safety_state && (
                        <Badge variant="outline" className="text-xs">
                          {selectedTask.target_worker.safety_state}
                        </Badge>
                      )}
                      {selectedTask.target_worker.current_risk_score && (
                        <span>Risk: {selectedTask.target_worker.current_risk_score}%</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Location Info for Inspect/Fix */}
            {selectedTask?.source_context?.location && (
              <div>
                <h4 className="text-sm font-medium mb-1">Location</h4>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <MapPin className="h-4 w-4" />
                  <span>{selectedTask.source_context.location}</span>
                  {selectedTask.source_context.hazard_category && (
                    <Badge variant="outline">{selectedTask.source_context.hazard_category}</Badge>
                  )}
                </div>
              </div>
            )}

            {/* Completion Notes */}
            {selectedTask?.status !== 'completed' && (
              <div>
                <h4 className="text-sm font-medium mb-1">Completion Notes</h4>
                <Textarea
                  placeholder="Add notes about how this action was completed..."
                  value={completionNotes}
                  onChange={(e) => setCompletionNotes(e.target.value)}
                  rows={3}
                />
              </div>
            )}

            {selectedTask?.status === 'completed' && selectedTask.completion_notes && (
              <div>
                <h4 className="text-sm font-medium mb-1">Completion Notes</h4>
                <p className="text-sm text-muted-foreground bg-muted/50 p-3 rounded-lg">
                  {selectedTask.completion_notes}
                </p>
              </div>
            )}
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setSelectedTask(null)}>
              Close
            </Button>
            {selectedTask?.status === 'pending' && (
              <Button 
                onClick={() => updateTaskMutation.mutate({ id: selectedTask.id, status: 'in_progress' })}
              >
                Start Task
              </Button>
            )}
            {selectedTask?.status === 'in_progress' && (
              <Button 
                onClick={() => updateTaskMutation.mutate({ 
                  id: selectedTask.id, 
                  status: 'completed',
                  notes: completionNotes
                })}
                className="bg-success hover:bg-success/90"
              >
                <CheckCircle2 className="h-4 w-4 mr-2" />
                Mark Complete
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Task Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Supervisor Action</DialogTitle>
            <DialogDescription>
              Create a new action for supervisors to complete
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium mb-1 block">Category</label>
              <Select 
                value={newTask.task_category} 
                onValueChange={(v) => setNewTask({...newTask, task_category: v as TaskCategory})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="fix">
                    <div className="flex items-center gap-2">
                      <Wrench className="h-4 w-4 text-orange-500" />
                      Fix - Repair or address hazard
                    </div>
                  </SelectItem>
                  <SelectItem value="coach">
                    <div className="flex items-center gap-2">
                      <Users className="h-4 w-4 text-purple-500" />
                      Coach - Worker intervention
                    </div>
                  </SelectItem>
                  <SelectItem value="inspect">
                    <div className="flex items-center gap-2">
                      <ClipboardCheck className="h-4 w-4 text-blue-500" />
                      Inspect - Check location/equipment
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm font-medium mb-1 block">Title</label>
              <Input
                value={newTask.title}
                onChange={(e) => setNewTask({...newTask, title: e.target.value})}
                placeholder="e.g., Inspect scaffolding at Area B"
              />
            </div>

            <div>
              <label className="text-sm font-medium mb-1 block">Description</label>
              <Textarea
                value={newTask.description}
                onChange={(e) => setNewTask({...newTask, description: e.target.value})}
                placeholder="Details about what needs to be done..."
                rows={3}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium mb-1 block">Priority</label>
                <Select 
                  value={newTask.priority} 
                  onValueChange={(v) => setNewTask({...newTask, priority: v})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="critical">Critical</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="low">Low</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Assign To</label>
                <Select 
                  value={newTask.assigned_to} 
                  onValueChange={(v) => setNewTask({...newTask, assigned_to: v})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select supervisor" />
                  </SelectTrigger>
                <SelectContent>
                    <SelectItem value="unassigned">Unassigned</SelectItem>
                    {supervisors?.map(sup => (
                      <SelectItem key={sup.id} value={sup.id}>
                        {sup.display_name || 'Unknown'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={() => createTaskMutation.mutate(newTask)}
              disabled={!newTask.title || createTaskMutation.isPending}
            >
              {createTaskMutation.isPending ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Creating...</>
              ) : (
                <>Create Action</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
